'use client'

import { XIcon } from 'lucide-react'
import React, { type InputHTMLAttributes, type ReactNode, useRef } from 'react'

import { cn } from './cn'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  leadingIcon?: ReactNode
  showResetHelper?: boolean
  trailingIcon?: ReactNode
  /**
   * Extra classes forwarded to the inner `<input>` (not the wrapper). Used
   * by LeadCapture to swap the border style for its dark-card context.
   */
  inputClassName?: string
}

export const Input = ({
  className,
  leadingIcon,
  showResetHelper = false,
  trailingIcon,
  type = 'text',
  inputClassName,
  ...props
}: InputProps): React.ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null)
  const hasTrailingContent = Boolean(trailingIcon || showResetHelper)

  const resetInput = (): void => {
    const input = inputRef.current

    if (!input) {
      return
    }

    const valueSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set

    valueSetter?.call(input, '')
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.focus()
  }

  return (
    <div
      className={cn(
        'snplay-can-focus-within snplay:relative snplay:flex',
        'snplay:w-full snplay:items-center snplay:rounded-lg',
        className,
      )}
    >
      {leadingIcon && (
        <span
          className={cn(
            'snplay:pointer-events-none snplay:absolute snplay:left-4',
            'snplay:flex snplay:items-center',
          )}
        >
          {leadingIcon}
        </span>
      )}
      <input
        ref={inputRef}
        type={type}
        className={cn(
          'snplay:h-14 snplay:w-full snplay:rounded-lg',
          'snplay:border snplay:border-ui-decoratorMedium',
          'snplay:bg-background snplay:px-4 snplay:text-base',
          'snplay:text-ui-primary snplay:transition',
          'snplay:outline-none snplay:placeholder:text-ui-secondary',
          'snplay:hover:border-ui-primary',
          'snplay:focus-visible:border-ui-primary snplay:focus-visible:outline-2',
          'snplay:focus-visible:outline-offset-2 snplay:focus-visible:outline-primary',
          leadingIcon && 'snplay:pl-12',
          hasTrailingContent && 'snplay:pr-14',
          trailingIcon && showResetHelper && 'snplay:pr-24',
          type === 'search' &&
            hasTrailingContent &&
            'snplay:[&::-webkit-search-cancel-button]:hidden',
          inputClassName,
        )}
        {...props}
      />
      {hasTrailingContent && (
        <span
          className={cn(
            'snplay:absolute snplay:right-3 snplay:flex',
            'snplay:items-center snplay:gap-1',
          )}
        >
          {trailingIcon}
          {showResetHelper && (
            <button
              type="button"
              onClick={resetInput}
              aria-label="Clear input"
              className={cn(
                'snplay-can-focus snplay:flex snplay:size-10',
                'snplay:cursor-pointer snplay:items-center',
                'snplay:justify-center snplay:rounded-full',
                'snplay:text-ui-secondary snplay:transition',
                'snplay:hover:bg-ui-decoratorLight snplay:hover:text-ui-primary',
              )}
            >
              <XIcon aria-hidden="true" className="snplay:size-4" />
              <span className="snplay:sr-only">Clear input</span>
            </button>
          )}
        </span>
      )}
    </div>
  )
}
